/**
 * Main Layout Component
 *
 * Provides the app structure with:
 * - Impersonation banner (when viewing as another user)
 * - Sidebar (reports list, navigation)
 * - TopBar (user info, logout)
 * - Main content area
 *
 * Applies client branding (color, logo)
 */
import { type ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useReports } from '../../hooks/useReports';

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  // Load reports globally so they're available in sidebar on all pages
  useReports();

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" dir="ltr">
      {/* Impersonation banner - shown at very top when impersonating */}
      <ImpersonationBanner />

      {/* Main app layout */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar - always on left */}
        <Sidebar />

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* TopBar */}
          <TopBar />

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
